import React, { useState, useEffect } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, message, DatePicker } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

const SanthaDonation = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedFilterMember, setSelectedFilterMember] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);

  useEffect(() => {
    fetchData();
    fetchMembers();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get("http://103.61.224.178:9876/member/getdonation");
      const fetchedData = response.data.data || [];
      setData(fetchedData);
      setFilteredData(fetchedData);
    } catch (error) {
      console.error("Error fetching donation data:", error);
      message.error("Failed to fetch donation data.");
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get("http://103.61.224.178:9876/member/get-all-members");
      const members = response.data.data || [];
      setMemberOptions(members.map((m) => ({ value: m._id, label: m.Name })));
    } catch (error) {
      console.error("Error fetching member data:", error);
      message.error("Failed to fetch member data.");
    }
  };

  const handleExportExcel = () => {
    const totalAmount = filteredData.reduce((sum, item) => {
      const amount = parseFloat(item.Amount) || 0;
      return sum + amount;
    }, 0);

    const excelData = filteredData.map((item, index) => ({
      "S.No": index + 1,
      "Member Name": memberOptions.find((m) => m.value === item.memberId?._id)?.label || "Unknown",
      "ACCI Code": item.memberId?.Accno || "N/A",
      Date: item.date,
      Remarks: item.remarks || "N/A",
      Amount: item.Amount,
    }));

    const totalRow = {
      "S.No": "",
      "Member Name": "",
      "ACCI Code": "",
      Date: "",
      Remarks: "Total",
      Amount: totalAmount.toFixed(2),
    };

    const worksheetData = [...excelData, totalRow];
    const ws = XLSX.utils.json_to_sheet(worksheetData);

    const range = XLSX.utils.decode_range(ws["!ref"]);
    const totalRowIndex = range.e.r;
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true },
        alignment: { horizontal: "right" },
      };
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Donations");
    XLSX.writeFile(wb, "Donations.xlsx");
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Donation Report", 14, 10);

    const tableColumn = ["S.No", "Member Name", "ACCI Code", "Amount", "Remarks", "Date"];
    const tableRows = filteredData.map((item, index) => [
      index + 1,
      memberOptions.find((m) => m.value === item.memberId?._id)?.label || "Unknown",
      item.memberId?.Accno || "N/A",
      item.Amount,
      item.remarks || "N/A",
      item.date,
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.save("Donations.pdf");
  };

  useEffect(() => {
    let filtered = data;

    if (selectedFilterMember) {
      filtered = filtered.filter((item) => item.memberId?._id === selectedFilterMember);
    }

    if (searchText.trim() !== "") {
      filtered = filtered.filter((item) =>
        (memberOptions.find((m) => m.value === item.memberId?._id)?.label || "Unknown")
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        (item.Amount?.toString().includes(searchText) || false) ||
        (item.remarks && item.remarks.toLowerCase().includes(searchText.toLowerCase()))
      );
    }

    setFilteredData(filtered);
  }, [searchText, selectedFilterMember, data, memberOptions]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`http://103.61.224.178:9876/member/deldonation/${id}`);
      message.success("Donation deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Error deleting donation:", error);
      message.error("Failed to delete donation.");
    }
  };

  const handleModalOpen = () => {
    setEditMode(false);
    setCurrentRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleFormSubmit = async (values) => {
    try {
      values.date = values.date.format("YYYY-MM-DD");
      const payload = {
        memberId: values.memberId,
        Amount: values.Amount,
        remarks: values.remarks,
        date: values.date,
      };

      if (editMode) {
        await axios.put(`http://103.61.224.178:9876/member/updonation/${currentRecord._id}`, payload);
        message.success("Donation updated successfully");
      } else {
        await axios.post("http://103.61.224.178:9876/member/createdonation", payload);
        message.success("Donation added successfully");
      }

      fetchData();
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error saving donation:", error);
      message.error("Failed to save donation.");
    }
  };

  const columns = [
    { title: "S.No", render: (_, __, index) => index + 1 },
    {
      title: "ACCI Code",
      dataIndex: ["memberId", "Accno"],
      key: "Accno",
      sorter: (a, b) => {
        const accnoA = a.memberId?.Accno || "";
        const accnoB = b.memberId?.Accno || "";
        return accnoA.localeCompare(accnoB);
      },
      defaultSortOrder: "ascend",
      render: (accno) => accno || "N/A",
    },
    {
      title: "Member Name",
      key: "memberName",
      render: (_, record) => memberOptions.find((m) => m.value === record.memberId?._id)?.label || "Unknown",
    },
    { title: "Amount", dataIndex: "Amount" },
    { title: "Remarks", dataIndex: "remarks", render: (text) => text || "N/A" },
    { title: "Date", dataIndex: "date" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditMode(true);
              setCurrentRecord(record);
              form.setFieldsValue({
                memberId: record.memberId?._id,
                Amount: record.Amount,
                remarks: record.remarks,
                date: moment(record.date),
              });
              setIsModalVisible(true);
            }}
          />
          <Button icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: "16px" }}>
        <Col span={4}>
          <Select
            placeholder="Filter by Member"
            allowClear
            style={{ width: "100%" }}
            onChange={setSelectedFilterMember}
            options={memberOptions}
          />
        </Col>
        <Col span={6}>
          <Input
            placeholder="Search Donations"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col>
          <Button icon={<FileExcelOutlined />} style={{ marginLeft: 8 }} onClick={handleExportExcel}>
            Export Excel
          </Button>
          <Button icon={<FilePdfOutlined />} style={{ marginLeft: 8 }} onClick={handleExportPDF}>
            Export PDF
          </Button>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Button icon={<PlusOutlined />} type="primary" onClick={handleModalOpen}>
            Add Donation
          </Button>
        </Col>
      </Row>
      <Table columns={columns} dataSource={filteredData} rowKey="_id" />

      <Modal
        title={editMode ? "Edit Donation" : "Add Donation"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item name="memberId" label="Member" rules={[{ required: true, message: "Please select a member" }]}>
            <Select
              showSearch
              placeholder="Select Member"
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={memberOptions}
            />
          </Form.Item>
          <Form.Item name="Amount" label="Amount" rules={[{ required: true, message: "Please enter an amount" }]}>
            <Input type="number" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="date" label="Date" rules={[{ required: true, message: "Please select a date" }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SanthaDonation;